import { useEffect, useRef, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  Button,
  Form,
  Grid,
  Image,
  Message,
  Segment,
  Divider,
} from "semantic-ui-react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import ruvixxLogo from "../../assets/ruvixx-logo-color.svg";
import { removeSession } from "../../helpers/sessionHelpers";
import Validate from "./../../components/Validate";
import AuthService from "./../../services/Auth";
import UserService from "./../../services/User";
import ChangePasswordForm from "./ChangePasswordForm";
import Input from "./Input";
import ResetPasswordModal from "./ResetPasswordModal";
import { useSocket } from "providers/SocketProvider";
import useTheme from "../../hooks/useTheme";
import { validatePassword } from "../../helpers/password";
import useAuthStore from "../../stores/useAuthStore";

const LoginForm = () => {
  const { isDark } = useTheme();
  const { createSocket } = useSocket();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [redirectToReferrer, setRedirectToReferrer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shouldChangePassword, setShouldChangePassword] = useState(
    () => !!useAuthStore.getState().shouldChangePassword
  );
  const [successMessage, setSuccessMessage] = useState("");
  const [showEmailLogin, setShowEmailLogin] = useState(false);

  // Magic token states
  const [magicTokenMode, setMagicTokenMode] = useState(false);
  const [magicToken, setMagicToken] = useState(null);
  const [tokenValidating, setTokenValidating] = useState(false);
  const [userFullName, setUserFullName] = useState("");

  const submitButton = useRef();
  const tokenValidated = useRef(false);
  const location = useLocation();

  const validateMagicToken = async token => {
    setTokenValidating(true);
    try {
      const result = await AuthService.validateMagicToken(token);
      if (result.valid) {
        setMagicTokenMode(true);
        setMagicToken(token);
        setEmail(result.email);
        setUserFullName(result.full_name);
      } else {
        setErrorMessage(
          "This password setup link is invalid or has expired. Please contact your administrator for assistance."
        );
      }
    } catch (error) {
      console.error("Token validation error:", error);
      setErrorMessage(
        error.message ||
          "This password setup link is invalid or has expired. Please contact your administrator for assistance."
      );
    } finally {
      setTokenValidating(false);
    }
  };

  // Check for magic token on mount
  useEffect(() => {
    const token = searchParams.get("token");
    if (token && !tokenValidated.current) {
      tokenValidated.current = true;
      validateMagicToken(token);
      // SECURITY: keep the one-time setup token out of the address bar, history,
      // and any outbound referrer. validateMagicToken has already captured it.
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.delete("token");
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  const handleMagicTokenPasswordSetup = async () => {
    setLoading(true);
    try {
      await AuthService.setupPasswordWithMagicToken(magicToken, newPassword);
      removeSession();
      createSocket();
      setRedirectToReferrer(true);
    } catch (error) {
      setErrorMessage(
        error.message || "Failed to set up password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthRedirectIfPending = () => {
    const oauthPending = sessionStorage.getItem("oauth_auth_pending");
    if (oauthPending) {
      sessionStorage.removeItem("oauth_auth_pending");
      try {
        const { callbackUri, state } = JSON.parse(oauthPending);
        if (!callbackUri || new URL(callbackUri).protocol !== "https:") {
          return false;
        }
        const params = new URLSearchParams();
        params.set("callback_uri", callbackUri);
        if (state) params.set("state", state);
        navigate(`/oauth-login?${params.toString()}`);
        return true;
      } catch {
        return false;
      }
    }
    return false;
  };

  const handleLogin = async () => {
    setLoading(true);
    if (!Validate.email(email)) {
      setErrorMessage("Email must be valid");
      setLoading(false);
    } else {
      try {
        await AuthService.login(email, password);
        checkShouldChangePassword();
        removeSession();
        createSocket();
      } catch (error) {
        setErrorMessage(error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChangePassword = async () => {
    setLoading(true);
    try {
      await UserService.editUserSettings({
        oldPassword: password,
        newPassword,
      });
      useAuthStore.getState().clearShouldChangePassword();
      setRedirectToReferrer(true);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    const submitBtn = submitButton.current.ref.current;
    if (!submitBtn.disabled) {
      submitBtn.click();
    }
  };

  const checkShouldChangePassword = async () => {
    const userSettings = await UserService.getUserSettings();
    if (userSettings.should_change_password) {
      setShouldChangePassword(true);
      useAuthStore.getState().setShouldChangePassword(true);
    } else {
      if (handleOAuthRedirectIfPending()) return;
      setRedirectToReferrer(true);
    }
  };

  const handleGoogleSuccess = async credentialResponse => {
    setLoading(true);
    setErrorMessage("");
    try {
      // credentialResponse.credential contains the JWT ID token
      await AuthService.googleLogin(credentialResponse.credential);
      removeSession();
      createSocket();
      if (handleOAuthRedirectIfPending()) return;
      setRedirectToReferrer(true);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    // User cancelled the sign-in flow or an error occurred
    // Only show error if it's not a user cancellation
    console.error("Google Sign-In failed or was cancelled");
    // Don't set error message as this is usually user-initiated cancellation
  };

  const renderLoginForm = () => {
    return (
      <>
        <Form>
          <Input
            label="Email"
            type="email"
            name="email"
            value={email}
            onChange={(_, { value }) => setEmail(value)}
            onSubmit={handleSubmit}
            autoFocus
          />
          <Input
            label="Password"
            type="password"
            name="password"
            value={password}
            onChange={(_, { value }) => setPassword(value)}
            onSubmit={handleSubmit}
          />
        </Form>

        <ResetPasswordModal handleResetSuccess={handleResetSuccess} />
        {successMessage && <Message success content={successMessage} />}
      </>
    );
  };

  const renderChangePasswordForm = () => {
    return (
      <ChangePasswordForm
        password={password}
        newPassword={newPassword}
        confirmPassword={confirmPassword}
        setPassword={setPassword}
        setNewPassword={setNewPassword}
        setConfirmPassword={setConfirmPassword}
        onSubmit={handleSubmit}
        hideCurrentPassword={magicTokenMode}
        customMessage={
          magicTokenMode
            ? "Please create a secure password for your account."
            : null
        }
        userFullName={magicTokenMode ? userFullName : null}
      />
    );
  };

  const renderSubmitButton = () => {
    let label = "Sign In";
    let disabled = !email || !password;
    let handler = handleLogin;

    if (magicTokenMode) {
      label = "Set Up Password";
      disabled =
        !newPassword ||
        newPassword !== confirmPassword ||
        !validatePassword(newPassword);
      handler = handleMagicTokenPasswordSetup;
    } else if (shouldChangePassword) {
      label = "Change Password";
      disabled =
        !password ||
        !newPassword ||
        newPassword === password ||
        newPassword !== confirmPassword ||
        !validatePassword(newPassword);
      handler = handleChangePassword;
    }

    return (
      <Segment basic textAlign="center">
        <Button
          ref={submitButton}
          primary
          size="tiny"
          type="submit"
          disabled={disabled || loading}
          loading={loading}
          content={label}
          onClick={handler}
        />
      </Segment>
    );
  };

  const handleResetSuccess = message => {
    setSuccessMessage(message);
  };

  const renderGoogleSignIn = () => {
    const googleClientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;

    if (!googleClientId || shouldChangePassword || magicTokenMode) {
      return null;
    }

    return (
      <Segment basic textAlign="center">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            theme={isDark ? "filled_black" : "outline"}
            size="medium"
            text="signin_with"
            shape="rectangular"
            logo_alignment="left"
          />
        </div>
      </Segment>
    );
  };

  const renderLoginToggle = () => {
    const googleClientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;

    if (!googleClientId || shouldChangePassword || magicTokenMode) {
      return null;
    }

    const linkStyle = {
      background: "none",
      border: "none",
      color: "var(--color-blue)",
      cursor: "pointer",
      font: "inherit",
      padding: 0,
    };

    return (
      <Segment basic textAlign="center">
        {showEmailLogin ? (
          <>
            <Divider horizontal>Or</Divider>
            {renderGoogleSignIn()}
          </>
        ) : (
          <>
            <Divider horizontal>Or</Divider>
            <button
              onClick={() => {
                setShowEmailLogin(true);
                setErrorMessage("");
              }}
              style={linkStyle}
              type="button"
            >
              Login with Email
            </button>
          </>
        )}
      </Segment>
    );
  };

  const { from } = location.state || {
    from: { pathname: "/campaigns" },
  };

  if (redirectToReferrer === true) {
    return <Navigate to={from} />;
  }

  // Show loading while validating magic token
  if (tokenValidating) {
    return (
      <Segment basic loading>
        <p>Validating your link...</p>
      </Segment>
    );
  }

  const googleClientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;

  const formContent =
    magicTokenMode || shouldChangePassword ? (
      <>
        {renderChangePasswordForm()}
        {errorMessage && <Message error content={errorMessage} />}
        {renderSubmitButton()}
      </>
    ) : googleClientId && !showEmailLogin ? (
      <>
        {renderGoogleSignIn()}
        {errorMessage && <Message error content={errorMessage} />}
        {renderLoginToggle()}
      </>
    ) : (
      <>
        {renderLoginForm()}
        {errorMessage && <Message error content={errorMessage} />}
        {renderSubmitButton()}
        {googleClientId && renderLoginToggle()}
      </>
    );

  const wrappedContent = googleClientId ? (
    <GoogleOAuthProvider clientId={googleClientId}>
      {formContent}
    </GoogleOAuthProvider>
  ) : (
    formContent
  );

  return (
    <Segment basic>
      <Grid>
        <Grid.Column width="16" textAlign="center">
          <Image src={ruvixxLogo} size="small" centered />
        </Grid.Column>
      </Grid>
      {wrappedContent}
    </Segment>
  );
};

export default LoginForm;
